'use client';

import { FC } from 'react';
import { Content } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';

/**
 * Props for `FeatureGroup`.
 */
export type FeatureGroupProps = SliceComponentProps<Content.FeatureGroupSlice>;

/**
 * Component for "FeatureGroup" Slices.
 */
const FeatureGroup: FC<FeatureGroupProps> = ({ slice }) => {
  const { features } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="features-section"
    >
      <div className="max-w-[1140px] mx-auto">
        <div className="flex flex-col gap-6 items-center">
          {features && features.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
              {features.map((feature, index) => (
                <div key={index} className="feat-card">
                  <div className="flex flex-col gap-2 items-start justify-center w-full">
                    {/* Icon */}
                    {feature.icon && (
                      <div className="w-12 h-12">
                        <PrismicNextImage
                          field={feature.icon}
                          className="w-full h-full object-contain"
                          fallbackAlt=""
                        />
                      </div>
                    )}

                    {feature.title && (
                      <p className="feat-title">{feature.title}</p>
                    )}

                    {feature.description && (
                      <div className="text-sm font-medium text-white leading-[1.5]">
                        <PrismicRichText
                          field={feature.description}
                          components={{
                            paragraph: ({ children }) => <p>{children}</p>,
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .features-section {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          align-items: flex-start;
          align-content: flex-start;
          padding: 40px 16px 60px;
          gap: 40px;
          background: linear-gradient(180deg, #0e1010 0%, #0f1211 53.29%);
        }
        .feat-card {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 20px;
          gap: 10px;
          background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.15) 0%,
            rgba(255, 255, 255, 0) 100%
          );
          border-radius: 16px;
          flex: none;
          order: 0;
          align-self: stretch;
          flex-grow: 1;
        }

        .feat-title {
          font-style: normal;
          text-transform: uppercase;
          font-weight: 600;
          font-size: 24px;
          line-height: 30px;
          letter-spacing: -1px;
          color: #05f273;
        }

        @media (min-width: 768px) {
          .features-section {
            padding: 60px 40px 80px;
            gap: 80px;
          }
          .feat-card {
            padding: 24px;
          }
          .feat-title {
            font-size: 28px;
            line-height: 34px;
          }
        }

        @media (min-width: 1024px) {
          .features-section {
            padding: 80px 200px 120px;
            gap: 120px;
          }
          .feat-title {
            font-size: 31px;
            line-height: 38px;
          }
        }
      `}</style>
    </section>
  );
};

export default FeatureGroup;
